import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Input,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";

const OffboardCustomerModal = ({ open, handleClose }) => {
  const colors = useColors();
  const {
    trackEvent,
    getCustomersWithSecretStatus,
    offboardCustomer,
    getProtectedCustomers,
  } = useAPI();

  const [customerId, setCustomerId] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [protectedCustomers, setProtectedCustomers] = useState([]);
  const [onOffboard, setOnOffboard] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [manualCustomerId, setManualCustomerId] = useState("");
  const [offboardingActions, setOffboardingActions] = useState({
    scheduleCustomerSecretsDeletion: true,
    removeFromPlatformConfig: true,
    deletePlatformUsers: true,
    updateJournalSecret: true,
    removeFromSupportLambdaSecret: true,
    scheduleSupportSecretsDeletion: false,
    deleteFromDynamoDB: false,
    deleteS3Bucket: false,
    deleteSupportS3Bucket: false,
  });

  // Fetch customer list and protected customers when modal opens
  useEffect(() => {
    if (open) {
      getCustomersWithSecretStatus()
        .then((response) => {
          setCustomerList(response.data);
        })
        .catch((error) => {
          Toast.error("Failed to fetch customer list");
        });

      getProtectedCustomers()
        .then((response) => {
          setProtectedCustomers(response.data);
        })
        .catch((error) => {
          Toast.error("Failed to fetch protected customers list");
        });
    }
  }, [open]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!open) {
      setCustomerId("");
      setConfirmText("");
      setIsConfirmed(false);
      setIsOtherSelected(false);
      setManualCustomerId("");
      setOffboardingActions({
        scheduleCustomerSecretsDeletion: true,
        removeFromPlatformConfig: true,
        deletePlatformUsers: true,
        updateJournalSecret: true,
        removeFromSupportLambdaSecret: true,
        scheduleSupportSecretsDeletion: false,
        deleteFromDynamoDB: false,
        deleteS3Bucket: false,
        deleteSupportS3Bucket: false,
      });
    }
  }, [open]);

  // Check if confirmation text matches
  useEffect(() => {
    setIsConfirmed(confirmText === "delete");
  }, [confirmText]);

  const handleCancel = () => {
    trackEvent("User cancelled the offboard customer dialog");
    handleClose();
  };

  const handleSelectChange = (event) => {
    const value = event.target.value;
    if (value === "other") {
      setIsOtherSelected(true);
      setCustomerId("");
    } else {
      setIsOtherSelected(false);
      setManualCustomerId("");
      setCustomerId(value);
    }
  };

  const handleManualInputChange = (event) => {
    setManualCustomerId(event.target.value);
  };

  const handleActionChange = (event) => {
    setOffboardingActions({
      ...offboardingActions,
      [event.target.name]: event.target.checked,
    });
  };

  const handleOffboard = () => {
    const idToOffboard = isOtherSelected ? manualCustomerId : customerId;
    if (!idToOffboard || !isConfirmed) return;

    // Check if customer is protected
    if (protectedCustomers.includes(idToOffboard)) {
      Toast.error(
        `Customer ${idToOffboard} is protected and cannot be offboarded`
      );
      return;
    }

    // Check if at least one action is selected
    if (
      !offboardingActions.scheduleCustomerSecretsDeletion &&
      !offboardingActions.scheduleSupportSecretsDeletion &&
      !offboardingActions.removeFromPlatformConfig &&
      !offboardingActions.deleteFromDynamoDB &&
      !offboardingActions.deletePlatformUsers &&
      !offboardingActions.updateJournalSecret &&
      !offboardingActions.removeFromSupportLambdaSecret &&
      !offboardingActions.deleteS3Bucket &&
      !offboardingActions.deleteSupportS3Bucket
    ) {
      Toast.error("Please select at least one offboarding action");
      return;
    }

    setOnOffboard(true);
    offboardCustomer(idToOffboard, offboardingActions)
      .then(() => {
        trackEvent(`Offboarded customer ${idToOffboard}`);
        Toast.success(`Customer ${idToOffboard} offboarded successfully`);
        handleClose();
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(
            error.response.data ||
              `Failed to offboard customer: access denied or insufficient permissions`
          );
        } else if (error.message) {
          Toast.error(`Failed to offboard customer: ${error.message}`);
        } else {
          Toast.error(`Failed to offboard customer ${idToOffboard}`);
        }
      })
      .finally(() => {
        setOnOffboard(false);
      });
  };

  // Check if the current customer ID exists in the list for manual entry validation
  const customerIdExists = (id) => {
    return customerList.some((customer) => customer.customer_id === id);
  };

  // Check if customer is protected
  const isCustomerProtected = (id) => {
    return protectedCustomers.includes(id);
  };

  // Get the customer object for the selected ID
  const getSelectedCustomer = () => {
    if (isOtherSelected) return null;
    return (
      customerList.find((customer) => customer.customer_id === customerId) ||
      null
    );
  };

  const selectedCustomer = getSelectedCustomer();
  const hasSecret = selectedCustomer ? selectedCustomer.has_secret : true;
  const isProtected = isOtherSelected
    ? isCustomerProtected(manualCustomerId)
    : isCustomerProtected(customerId);

  const isValidCustomer = isOtherSelected
    ? manualCustomerId &&
      !customerIdExists(manualCustomerId) &&
      !isCustomerProtected(manualCustomerId)
    : customerId &&
      customerIdExists(customerId) &&
      !isCustomerProtected(customerId);

  // Allow customers without secrets to be selected
  const customerOptions = customerList.map((customer) => {
    const isProtected = isCustomerProtected(customer.customer_id);
    return (
      <MenuItem
        key={customer.customer_id}
        value={customer.customer_id}
        disabled={isProtected}
        sx={{
          color: isProtected
            ? colors.error_main
            : customer.has_secret
            ? "inherit"
            : colors.grey_60percent,
        }}
      >
        {customer.customer_id}
        {!customer.has_secret && !isProtected && (
          <Typography
            component="span"
            sx={{
              ml: 1,
              color: colors.grey_60percent,
              fontSize: "11px",
              fontStyle: "italic",
            }}
          >
            (no secret found)
          </Typography>
        )}
        {isProtected && (
          <Typography
            component="span"
            sx={{
              ml: 1,
              color: colors.error_main,
              fontSize: "11px",
              fontWeight: "bold",
            }}
          >
            (protected)
          </Typography>
        )}
      </MenuItem>
    );
  });

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
    >
      <Box
        sx={{
          width: 500,
          bgcolor: "background.paper",
          margin: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 24,
          overflowY: "auto",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: colors.common_white,
              pt: "5%",
            }}
            id="modal-modal-title"
          >
            <Typography variant="subtitle1" gutterBottom>
              Offboard Customer
            </Typography>
            <Typography
              variant="body1"
              gutterBottom
              sx={{
                marginTop: "4px",
                color: colors.error_main,
                fontWeight: "bold",
              }}
            >
              DANGER: This operation is irreversible
            </Typography>
          </Box>
          <Box sx={{ padding: "32px" }}>
            {/* Customer ID */}
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Customer ID
              </Typography>
              <Select
                value={isOtherSelected ? "other" : customerId}
                onChange={handleSelectChange}
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                  },
                  marginBottom: "8px",
                }}
              >
                <MenuItem value="" disabled>
                  Select a customer ID
                </MenuItem>
                {customerOptions}
                <MenuItem value="other">Other (manual entry)</MenuItem>
              </Select>

              {isOtherSelected && (
                <Input
                  placeholder="Enter customer ID manually"
                  value={manualCustomerId}
                  onChange={handleManualInputChange}
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${
                        manualCustomerId
                          ? isCustomerProtected(manualCustomerId)
                            ? colors.error_main
                            : colors.grey_32percent
                          : colors.error_main
                      }`,
                      fontSize: "12px",
                    },
                    marginTop: "8px",
                  }}
                />
              )}

              {isOtherSelected &&
                manualCustomerId &&
                customerIdExists(manualCustomerId) && (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.error_main }}
                  >
                    This customer ID exists in the dropdown - please select it
                    from there
                  </Typography>
                )}

              {isOtherSelected &&
                manualCustomerId &&
                isCustomerProtected(manualCustomerId) && (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.error_main, fontWeight: "bold" }}
                  >
                    This customer is protected and cannot be offboarded
                  </Typography>
                )}
            </FormControl>

            <Divider style={{ marginBottom: "16px", marginTop: "16px" }} />

            {isValidCustomer && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Select offboarding actions to perform:
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            offboardingActions.scheduleCustomerSecretsDeletion
                          }
                          onChange={handleActionChange}
                          name="scheduleCustomerSecretsDeletion"
                          disabled={!hasSecret}
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Schedule customer secret for deletion (30-day window)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offboardingActions.removeFromPlatformConfig}
                          onChange={handleActionChange}
                          name="removeFromPlatformConfig"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Remove the customer from nightly san
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offboardingActions.deletePlatformUsers}
                          onChange={handleActionChange}
                          name="deletePlatformUsers"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Delete platform users (block customer login)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offboardingActions.updateJournalSecret}
                          onChange={handleActionChange}
                          name="updateJournalSecret"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Remove from journal secret
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            offboardingActions.scheduleSupportSecretsDeletion
                          }
                          onChange={handleActionChange}
                          name="scheduleSupportSecretsDeletion"
                          disabled={!hasSecret}
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Schedule support.customer secret for deletion (30-day
                          window)
                        </Typography>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offboardingActions.deleteFromDynamoDB}
                          onChange={handleActionChange}
                          name="deleteFromDynamoDB"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Delete all customer data from DynamoDB tables
                        </Typography>
                      }
                    />
                    {offboardingActions.deleteFromDynamoDB && (
                      <Box
                        sx={{
                          ml: 4,
                          p: 1,
                          backgroundColor: colors.error_light,
                          borderLeft: `4px solid ${colors.error_main}`,
                          borderRadius: "4px",
                        }}
                      >
                        <Typography variant="caption" color={colors.error_dark}>
                          WARNING: This operation will permanently delete data,
                          and cannot be undone. Use with extreme caution.
                        </Typography>
                      </Box>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offboardingActions.deleteS3Bucket}
                          onChange={handleActionChange}
                          name="deleteS3Bucket"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Delete S3 bucket and all of its content
                        </Typography>
                      }
                    />
                    {offboardingActions.deleteS3Bucket && (
                      <Box
                        sx={{
                          ml: 4,
                          p: 1,
                          backgroundColor: colors.error_light,
                          borderLeft: `4px solid ${colors.error_main}`,
                          borderRadius: "4px",
                        }}
                      >
                        <Typography variant="caption" color={colors.error_dark}>
                          WARNING: This will permanently delete the S3 bucket
                          and all customer data stored in it. This operation
                          cannot be undone.
                        </Typography>
                      </Box>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offboardingActions.deleteSupportS3Bucket}
                          onChange={handleActionChange}
                          name="deleteSupportS3Bucket"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="12px">
                          Delete support S3 bucket (support.customer_id) and all
                          of its contents
                        </Typography>
                      }
                    />
                    {offboardingActions.deleteSupportS3Bucket && (
                      <Box
                        sx={{
                          ml: 4,
                          p: 1,
                          backgroundColor: colors.error_light,
                          borderLeft: `4px solid ${colors.error_main}`,
                          borderRadius: "4px",
                        }}
                      >
                        <Typography variant="caption" color={colors.error_dark}>
                          WARNING: This will permanently delete the
                          support.customer_id S3 bucket and all customer data
                          stored in it. This operation cannot be undone.
                        </Typography>
                      </Box>
                    )}
                  </FormGroup>
                </Box>

                <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontSize={"14px"}>
                    Confirm by typing: "delete"
                  </Typography>
                  <Input
                    placeholder="Type confirmation text"
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                    disableUnderline
                    sx={{
                      "& .MuiInput-input": {
                        borderBottom: `1px solid ${
                          isConfirmed ? colors.success_main : colors.error_main
                        }`,
                        fontSize: "12px",
                      },
                    }}
                  />
                </FormControl>
              </>
            )}

            {isProtected && (
              <Box
                sx={{
                  p: 2,
                  mt: 2,
                  backgroundColor: colors.error_light,
                  borderLeft: `4px solid ${colors.error_main}`,
                  borderRadius: "4px",
                }}
              >
                <Typography
                  variant="body2"
                  color={colors.error_dark}
                  fontWeight="bold"
                >
                  This customer is protected and cannot be offboarded
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <SecondaryButton
              onClick={handleCancel}
              sx={{
                color: colors.primary_main,
                borderRadius: "10px",
                border: "none",
                height: "30px",
                fontSize: "12px",
              }}
            >
              Cancel
            </SecondaryButton>
            {onOffboard ? (
              <LoadingIcon />
            ) : (
              <PrimaryButton
                onClick={handleOffboard}
                disabled={!isConfirmed || !isValidCustomer || onOffboard}
                style={{
                  color: colors.common_white,
                  backgroundColor:
                    isConfirmed && isValidCustomer && !onOffboard
                      ? colors.error_main
                      : colors.grey_40percent,
                  borderRadius: "10px",
                  border: "none",
                  cursor:
                    isConfirmed && isValidCustomer && !onOffboard
                      ? "pointer"
                      : "not-allowed",
                }}
                size="small"
              >
                Offboard Customer
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default OffboardCustomerModal;
